import { Download, MessageSquareText, RefreshCw, Trash2 } from 'lucide-react';
import { StatTile } from '../../portal';
import { formatCompactNumber } from './overviewSummary';

interface AnalyticsMetric {
  value: number | null;
  suppressed: boolean;
}

export interface OverviewAnalytics {
  installs: AnalyticsMetric;
  uninstalls: AnalyticsMetric;
  updates: AnalyticsMetric;
}

interface OverviewStatsProps {
  /** null = analytics not available for this viewer/environment. */
  analytics: OverviewAnalytics | null;
  totalReviews: number | null;
  reviewsThisWeek: number | null;
  isLoading: boolean;
}

/**
 * Keyed on the text so a number fades in when it first lands or changes
 * (analytics arriving after the tiles) but not on identical refetches.
 */
function fadeValue(text: string) {
  return (
    <span key={text} className="motion-enter inline-block">
      {text}
    </span>
  );
}

const UNAVAILABLE = {
  value: fadeValue('—'),
  caption: 'Not available yet',
  valueClassName: 'text-seeker-400',
};

function analyticsTile(metric?: AnalyticsMetric) {
  const value = metric?.value;
  return value == null
    ? {
        ...UNAVAILABLE,
        caption: metric?.suppressed
          ? 'Not enough activity in the last 7 days to report'
          : UNAVAILABLE.caption,
      }
    : { value: fadeValue(formatCompactNumber(value)), caption: 'Last 7 days' };
}

export function OverviewStats({
  analytics,
  totalReviews,
  reviewsThisWeek,
  isLoading,
}: OverviewStatsProps) {
  return (
    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
      <StatTile
        icon={<Download />}
        label="Installs"
        isLoading={isLoading}
        {...analyticsTile(analytics?.installs)}
      />
      <StatTile
        icon={<Trash2 />}
        label="Uninstalls"
        isLoading={isLoading}
        {...analyticsTile(analytics?.uninstalls)}
      />
      <StatTile
        icon={<RefreshCw />}
        label="Updates"
        isLoading={isLoading}
        {...analyticsTile(analytics?.updates)}
      />
      <StatTile
        icon={<MessageSquareText />}
        label="Reviews"
        isLoading={isLoading}
        {...(totalReviews == null
          ? UNAVAILABLE
          : {
              value: fadeValue(formatCompactNumber(totalReviews)),
              caption:
                reviewsThisWeek != null ? `${reviewsThisWeek} this week` : null,
            })}
      />
    </div>
  );
}
